export const MODULE_CONTRACT_VERSION = '1.0.0' as const;
export const MODULE_SDK_VERSION = '1.0.0' as const;

export type SemanticVersion = `${number}.${number}.${number}${string}`;
export type VersionRange = string;
export type ModuleState = 'discovered' | 'validating' | 'resolved' | 'activating' | 'active' | 'degraded' | 'blocked' | 'failed' | 'stopping' | 'stopped';
export type ContributionKind = 'route' | 'navigation' | 'dashboard-widget' | 'settings-section' | 'component' | 'query' | 'action' | 'notification-handler';
export type FeatureFlagState = 'enabled' | 'disabled' | 'experimental';
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | { readonly [key: string]: JsonValue } | readonly JsonValue[];

declare const serviceType: unique symbol;
export interface ServiceToken<T> { readonly id: string; readonly [serviceType]?: T }
export function createServiceToken<T>(id: string): ServiceToken<T> {
  if (!/^[a-z][a-z0-9.-]+$/.test(id)) throw new Error(`Invalid service token: ${id}`);
  return Object.freeze({ id });
}

// Intentionally open for declaration merging by independently installed modules.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PlatformEventMap {}
export interface RuntimeEventMap {
  'runtime.module.state-changed': { moduleId: string; previous: ModuleState | null; current: ModuleState; reason?: string };
  'runtime.contribution.failed': { moduleId: string; contributionId: string; error: unknown };
}
// Intentionally open for declaration merging by independently installed modules.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PlatformCommandMap {}
export interface CommandContract<Payload = void, Result = void> { payload: Payload; result: Result }

export interface EventBusPort<Events extends object = PlatformEventMap & RuntimeEventMap> {
  on<K extends Extract<keyof Events, string>>(event: K, listener: (payload: Events[K]) => void | Promise<void>): () => void;
  emit<K extends Extract<keyof Events, string>>(event: K, payload: Events[K]): Promise<void>;
}
export interface CommandBusPort<Commands extends object = PlatformCommandMap> {
  register<K extends Extract<keyof Commands, string>>(command: K, handler: (payload: Commands[K] extends CommandContract<infer P, unknown> ? P : never) => Promise<Commands[K] extends CommandContract<unknown, infer R> ? R : never>): () => void;
  execute<K extends Extract<keyof Commands, string>>(command: K, payload: Commands[K] extends CommandContract<infer P, unknown> ? P : never): Promise<Commands[K] extends CommandContract<unknown, infer R> ? R : never>;
}
export interface ServiceContainerPort {
  provide<T>(token: ServiceToken<T>, value: T, owner?: string): void;
  provideFactory<T>(token: ServiceToken<T>, factory: (container: ServiceContainerPort) => T | Promise<T>, owner?: string): void;
  resolve<T>(token: ServiceToken<T>): Promise<T>;
  has(token: ServiceToken<unknown>): boolean;
}
export interface FeatureContext { readonly roles?: readonly string[]; readonly attributes?: Readonly<Record<string, JsonPrimitive>> }
export interface FeatureFlagDefinition { id: string; state: FeatureFlagState; allowedRoles?: readonly string[]; description?: string; owner?: string; expiresAt?: string }
export interface FeatureFlagPort { isEnabled(id: string | undefined, context?: FeatureContext): boolean }
export interface HealthCheckResult { status: 'healthy' | 'degraded' | 'unhealthy'; message?: string; details?: Readonly<Record<string, JsonValue>> }
export interface HealthPort { report(moduleId: string, result: HealthCheckResult): void }
export interface ObservationAttributes { readonly [key: string]: JsonPrimitive | undefined }
export interface SpanPort { setAttribute(name: string, value: JsonPrimitive): void; recordError(error: unknown): void; end(): void }
export interface ObservabilityPort {
  debug(message: string, attributes?: ObservationAttributes): void;
  info(message: string, attributes?: ObservationAttributes): void;
  warn(message: string, attributes?: ObservationAttributes): void;
  error(message: string, error?: unknown, attributes?: ObservationAttributes): void;
  startSpan(name: string, attributes?: ObservationAttributes): SpanPort;
}

export interface ModuleDependency { id: string; version: VersionRange; optional?: boolean }
export interface PermissionDefinition { code: `${string}.${string}`; name: string; description?: string; group: string }
export interface LazyContributionDefinition {
  id: string;
  kind: ContributionKind;
  order?: number;
  permission?: `${string}.${string}`;
  featureFlag?: string;
  load: () => Promise<unknown>;
  metadata?: Readonly<Record<string, JsonValue>>;
}
export interface RouteContributionDefinition extends LazyContributionDefinition { kind: 'route'; path: string; title: string }
export interface NavigationContributionDefinition extends LazyContributionDefinition { kind: 'navigation' }
export interface DashboardWidgetContributionDefinition extends LazyContributionDefinition { kind: 'dashboard-widget'; title: string; size?: 'sm' | 'md' | 'lg' | 'full' }
export interface SettingsContributionDefinition extends LazyContributionDefinition { kind: 'settings-section'; title: string }
export type ModuleContribution = LazyContributionDefinition | RouteContributionDefinition | NavigationContributionDefinition | DashboardWidgetContributionDefinition | SettingsContributionDefinition;
export interface ServiceProviderDefinition<T = unknown> { token: ServiceToken<T>; factory: (container: ServiceContainerPort) => T | Promise<T> }

export interface ModuleContext {
  readonly moduleId: string;
  readonly services: ServiceContainerPort;
  readonly events: EventBusPort;
  readonly commands: CommandBusPort;
  readonly features: FeatureFlagPort;
  readonly health: HealthPort;
  readonly observability: ObservabilityPort;
  readonly signal: AbortSignal;
}
export interface OchalModuleManifest {
  contractVersion: typeof MODULE_CONTRACT_VERSION;
  sdkVersion: VersionRange;
  runtimeVersion: VersionRange;
  id: string;
  name: string;
  version: SemanticVersion;
  enabled: boolean;
  featureFlag?: string;
  dependencies?: readonly ModuleDependency[];
  permissions?: readonly PermissionDefinition[];
  contributions?: readonly ModuleContribution[];
  services?: readonly ServiceProviderDefinition[];
  activate?: (context: ModuleContext) => void | Promise<void>;
  deactivate?: (context: ModuleContext) => void | Promise<void>;
  metadata?: Readonly<Record<string, JsonValue>>;
}
export interface PluginCatalogEntry { id: string; load: () => Promise<OchalModuleManifest> }
export interface PluginCatalog { readonly entries: readonly PluginCatalogEntry[] }
export function definePluginCatalog(entries: readonly PluginCatalogEntry[]): PluginCatalog { return Object.freeze({ entries: Object.freeze([...entries]) }); }
